import { Injectable, Injector } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable } from 'rxjs';

import { TeamType } from '../models/enums';
import { User } from '../models/user.model';

import { ConfigurationService } from './configuration.service';
import { EndpointFactory } from './endpoint-factory.service';
import { TeamQueueEdit } from '../models/team/team-queue-edit.model';


@Injectable({ providedIn: 'root'})
export class TeamEndpoint extends EndpointFactory {

  private readonly _teamsUrl: string = '/api/team';

  get teamsUrl() { return this.configurations.baseUrl + this._teamsUrl; }

  constructor(http: HttpClient, configurations: ConfigurationService, injector: Injector) {
    super(http, configurations, injector);
  }

  getTeamsEndpoint<T>(teamTypeId: TeamType, activeProjectId: number): Observable<T> {
    const options = { ...this.getRequestHeaders() };
    if (teamTypeId) { options['params'] = { teamTypeId, activeProjectId }; }

    return this.http.get<T>(this.teamsUrl, options);
  }

  getInactiveUsersEndpoint<T>(teamTypeId?: TeamType): Observable<T> {
    const endpointUrl = `${this.teamsUrl}/users/inactive/`;
    const options = { ...this.getRequestHeaders() };
    if (teamTypeId) { options['params'] = { teamTypeId }; }

    return this.http.get<T>(endpointUrl, options);
  }

  getTeamLabelsEndpoint<T>(teamTypeId: TeamType, activeProjectWfId: number): Observable<T> {
    const endpointUrl = `${this.teamsUrl}/labels`;
    const options = { ...this.getRequestHeaders() };
    options['params'] = { teamTypeId, activeProjectWfId };

    return this.http.get<T>(endpointUrl, options);
  }

  getAddTeamEndpoint<T>(users: User[], teamLabel: string, teamTypeId: TeamType, odpProjectWfId: number): Observable<T> {
    const body = { userIds: users.map(u => u.id), teamLabel: teamLabel };
    const options = { ...this.getRequestHeaders() };
    options['params'] = { teamTypeId, odpProjectWfId };

    return this.http.post<T>(this.teamsUrl, body, options);
  }

  getDeleteTeamEndpoint<T>(teamId: number): Observable<T> {
    const endpointUrl = `${this.teamsUrl}/${teamId}`;
    return this.http.delete<T>(endpointUrl, this.getRequestHeaders());
  }

  isUserInTeamEndpoint<T>(teamTypeId?: TeamType): Observable<T> {
    const endpointUrl = `${this.teamsUrl}/users/inteam`;
    const options = { ...this.getRequestHeaders() };
    if (teamTypeId) { options['params'] = { teamTypeId }; }

    return this.http.get<T>(endpointUrl, options);
  }

  getTeamAbstractStatusEndpoint<T>(teamId: number): Observable<T> {
    const endpointUrl = `${this.teamsUrl}/groups/${teamId}/status`;
    const options = { ...this.getRequestHeaders() };
    return this.http.get<T>(endpointUrl, options);
  }

  getAbstractGroupsByTeamEndpoint<T>(teamId: number): Observable<T> {
    const endpointUrl = `${this.teamsUrl}/groups/${teamId}`;
    const options = { ...this.getRequestHeaders() };
    return this.http.get<T>(endpointUrl, options);
  }

  saveTeamQueueChangesEndpoint<T>(model: TeamQueueEdit, teamId: number): Observable<T> {
    const endpointUrl = `${this.teamsUrl}/groups/queue/save/${teamId}`;
    const options = { ...this.getRequestHeaders() };
    const body = model;

    return this.http.post<T>(endpointUrl, body, options);
  }

  enqueueGroupsByTeamEndpoint<T>(teamId: number, activeProjectId: number, numGroups: number): Observable<T> {
    const endpointUrl = `${this.teamsUrl}/groups/${teamId}/${activeProjectId}/enqueue/${numGroups}`;
    const options = { ...this.getRequestHeaders() };

    return this.http.get<T>(endpointUrl, options);
  }

  getMaxAbstractGroupsEndpoint<T>(teamId: number, activeProjectId: number): Observable<T> {
    const endpointUrl = `${this.teamsUrl}/groups/${teamId}/${activeProjectId}/max`;
    const options = { ...this.getRequestHeaders() };
    return this.http.get<T>(endpointUrl, options);
  }

}
